import React, { useState } from 'react';
import { DisplayedProduct, SearchedProductName, ETIMFeature } from '../types/models';
import { ApiService } from '../services/api'; // Импорт ApiService
import ProductDetailsModal from './ProductDetailsModal'; // Импорт ProductDetailsModal
import { GlobeAltIcon } from '@heroicons/react/24/outline'; // Импорт иконки глобуса

interface ProductVariantCardProps {
  product: DisplayedProduct;
  isSelected: boolean;
  onSelect: (productCode: string, product: DisplayedProduct) => void; // Обновленная сигнатура
  onDeselect: () => void;
  spn: SearchedProductName; // Добавлен проп spn
}

const ProductVariantCard: React.FC<ProductVariantCardProps> = ({
  product,
  isSelected,
  onSelect,
  onDeselect,
  spn // Получаем spn из пропсов
}) => {
  const [isHovered, setIsHovered] = useState(false); // Состояние для отслеживания наведения
  const [isModalOpen, setIsModalOpen] = useState(false); // Состояние для управления видимостью модального окна
  const [productDetails, setProductDetails] = useState<any>(null); // Состояние для хранения деталей продукта

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const handleToggleSelection = () => {
    if (isSelected) {
      console.log(`Deselecting product: ${product.code}`);
      onDeselect();
    } else {
      console.log(`Selecting product: ${product.code}`);
      onSelect(product.code, product); // Передаем и код, и объект продукта
    }
  };

  const handleShowDetails = async () => {
    // Проверяем, если у продукта есть source_url, открываем его в новой вкладке
    if (product.source_url) {
      window.open(product.source_url, '_blank');
      return;
    }

    // Если source_url нет, загружаем детали через API как раньше
    setIsModalOpen(true);
    try {
      const details = await ApiService.getProductDetails(product.code);
      setProductDetails(details);
    } catch (error) {
      console.error('Ошибка при загрузке деталей продукта:', error);
      // TODO: Обработать ошибку, возможно, показать уведомление пользователю
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setProductDetails(null); // Очистить детали при закрытии
  };

  // Функция для поиска имени характеристики по коду
  const getFeatureName = (featureCode: string): string => {
    const etimFeature = spn.etim_features.find(f => f.FEATURE_CODE === featureCode);
    return etimFeature ? etimFeature.name : featureCode; // Возвращаем имя или код как запасной вариант
  };

  return (
    <div
      className={`
        border rounded-lg p-2 mb-2 transition-all duration-200 relative
        ${isSelected
          ? 'border-green-500 bg-green-50'
          : 'border-gray-200 hover:border-blue-300 hover:shadow-md'}
      `}
      onMouseEnter={handleMouseEnter} // Обработчик наведения
      onMouseLeave={handleMouseLeave} // Обработчик ухода мыши
    >
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <h5 className="font-medium text-sm">{product.description}</h5>

          <div className="flex flex-wrap gap-x-6 mt-2">
            <p className="text-xs text-gray-600">
              <span className="font-semibold">Производитель:</span> {product.brand}
            </p>
            <p className="text-xs text-gray-600">
              <span className="font-semibold">aрт.:</span> {product.vendor_code}
            </p>
            {product.price != null && (
              <p className="text-xs text-gray-600">
                <span className="font-semibold">Цена:</span>{' '}
                {product.price === 0
                  ? 'по запросу'
                  : `${product.price}${product.currency ? ` ${product.currency}` : ''}`}
              </p>
            )}
          </div>

          {/* Пометка для продуктов из интернета */}
          {product.source_url && (
            <div className="flex items-center mt-2">
              <GlobeAltIcon className="h-3 w-3 text-blue-500 mr-1" />
              <span className="text-xs text-blue-600 font-medium">Найден в интернете</span>
            </div>
          )}

          {/* Отдельным блоком выводим описание продукта */}
          {/* {product.description && (
            <div className="mt-2 text-sm text-gray-600">
              <span className="font-semibold block">Описание:</span>
              <p className="whitespace-pre-line">{product.description}</p>
            </div>
          )} */}

          {product.notes && (
            <div className="mt-2 text-sm text-gray-600">
              <span className="font-semibold block">Примечания:</span>
              <p className="whitespace-pre-line">{product.notes}</p>
            </div>
          )}
        </div>

        <div>
          {/* Новая кнопка "Детали" */}
          <button
            onClick={handleShowDetails}
            className="px-3 py-1 mr-2 rounded text-sm bg-gray-300 hover:bg-gray-400 text-gray-800"
            data-testid="details-button"
            title={product.source_url ? "Открыть страницу товара в интернете" : "Детали продукта"}
          >
            Детали
          </button>
          <button
            onClick={handleToggleSelection}
            className={`
              px-3 py-1 rounded text-sm
              ${isSelected
                ? 'bg-red-500 hover:bg-red-600 text-white'
                : 'bg-blue-500 hover:bg-blue-600 text-white'}
            `}
            data-testid="select-variant-button"
          >
            {isSelected ? 'Отменить выбор' : 'Выбрать'}
          </button>
        </div>
      </div>

      {/* Блок отображения характеристик при наведении */}
      {isHovered && product.features && product.features.length > 0 && (
        <div className="bg-gray-100 p-2 text-xs rounded-b-lg opacity-95 ">
          <p className="font-semibold mb-1">Примененные характеристики:</p>
          <ul className="list-disc list-inside">
            {product.features.map((feature, index) => (
              <li key={index}>
                <span className="font-medium">{feature.name}:</span> {feature.VALUE}
              </li>
            ))}
          </ul>
        </div>
      )}

      {isModalOpen && (
        <ProductDetailsModal
          productDetails={productDetails}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default ProductVariantCard;